<svelte:options accessors />

<script lang="ts">
    import Row from './Row.svelte';
    import Input from './Input.svelte';
    import Select from './Select.svelte';
    import { checkAddress, clearAddress, addressStore } from '../api/address.ts';
    import * as t from '../translations/Address.ts';
    import type { Address } from '../api/address.ts';
    let _class = '';
    export { _class as class };
    
    let searches = 0;
    export let isLoading = false;
    export let selectedAddress: Address | null = null;
    export let addressType;
    export let prefix;
    export let onClearAddress = () => {};
    
    let cities: string[] =
        selectedAddress?.alternateCities?.length ? selectedAddress?.alternateCities?.map(x => x.Key) 
        : selectedAddress?.city ? [selectedAddress?.city]
        : [];

    let addressResponseMessage = '';
    let suggestedZipCode = '';
    let streetAddress = '';
    let zipCode = '';
    let city = '';
    let state = '';
    let checksum = undefined;
    
    const setAddress = (addresses) => {
        let address : Address;
        if (addresses) {
            address = addresses.get(addressType);
        }
        selectedAddress = address;
        streetAddress = address?.streetAddress || '';
        zipCode = address?.zipCode || '';
        city = address?.city || '';
        cities = selectedAddress?.alternateCities?.length ? selectedAddress?.alternateCities?.map(x => x.Key)
            : selectedAddress?.city ? [selectedAddress?.city]
                : [];
        state = address?.state || '';
        checksum = address?.checksum;
    };
    setAddress($addressStore.addresses);

    $: setAddress($addressStore.addresses);
    
    const clearAll = () => {
        selectedAddress = null;        
        addressResponseMessage = '';
        suggestedZipCode = '';
        checksum = undefined;
        streetAddress = '';
        zipCode = '';
        cities = [];
        city = '';
        state = '';
        clearAddress(addressType);
        onClearAddress?.(addressType);

        $addressStore.addresses.delete(addressType);
        if ($addressStore.selectedAddress?.addressType === addressType) {
            $addressStore.selectedAddress = undefined;
        }
    };

    let timer: NodeJS.Timeout;
    const debounce = () => {
        cities = [];
        city = '';
        state = '';
        checksum = undefined;
        selectedAddress = null;
        
        if (!streetAddress || !zipCode) {
            return; /* quit early */
        }

        clearTimeout(timer);
        timer = setTimeout(addressChanged, 1000);
    };
    export let addressChanged = async (forceStreetAddress, forceZipCode, forceCity, forceState) => {               
        try {
            isLoading = true;
            searches += 1;
            const currentSearches = searches;
            const addressesResponse = await checkAddress(forceStreetAddress ?? streetAddress, forceZipCode ?? zipCode, forceCity, forceState);
            // Only process the answer if no new search was queued in the meantime.
            if (searches === currentSearches) {
                selectedAddress = null;
                cities = [];
                addressResponseMessage = '';
                suggestedZipCode = '';
                checksum = undefined;
                
                if (addressesResponse.message) {
                    addressResponseMessage = addressesResponse.message;
                    suggestedZipCode = addressesResponse.suggestedZipCode ?? '';
                    // TODO - Handle Google Map picker
                } else if (addressesResponse.addresses.length === 1) {
                    selectedAddress = addressesResponse.addresses[0];  
                    if (selectedAddress.alternateCities?.length) {
                        selectedAddress.alternateCities.forEach(c => {
                            cities.push(c.Key);
                        });
                    } else if (selectedAddress.city) {
                        cities.push(selectedAddress.city);
                    }
                } else {
                    // TODO - Handle multiple return addresses ?
                }

                if (selectedAddress) {
                    selectedAddress.addressType = addressType;
                    streetAddress = selectedAddress.streetAddress || "";
                    zipCode = selectedAddress.zipCode || "";
                    city = selectedAddress.city || "";
                    state = selectedAddress.state || "";
                    checksum = selectedAddress.checksum;
                    
                    $addressStore.addresses.set(selectedAddress.addressType, selectedAddress);
                }
            }
        } catch (error) {            
            console.error(error);
        } finally {
            isLoading = false;
        }
    }

/* Example Address
20000 Bass Pro Dr, Spanish Fort, AL 36527
*/

    const setCity = () => {
        if (selectedAddress) {
            selectedAddress = {
                ...selectedAddress,
                city,
            };

            addressChanged(undefined, undefined, city, state);
        }
    }
    
    let streetAddressLabel;
    let zipLabel;
    
    $: streetAddressLabel = ((prefix ?? '') + ' ' + t.StreetAddress()).trim();
    $: zipLabel = ((prefix ?? '') + ' ' + t.Zip()).trim();
</script>

<div class="w-full {_class}">
    {#if isLoading}
        <div>{t.Loading()}</div>
    {:else if checksum}
        <div class="text-base text-vv-green">
            {t.AddressOkay()}
        </div>
    {:else}
        <div class="text-base text-vv-red-fire">
            <!-- {t.AddressNotOkay()} -->
            {addressResponseMessage}
            {suggestedZipCode}
            &nbsp;
        </div>
    {/if}
    <Row>
        <Input fieldsetClass="w-full" placeholder={t.StreetAddressPlaceholder()}
            bind:value={streetAddress}            
            on:input={debounce}
            on:change={debounce}>
            <span slot="label">
                {streetAddressLabel}
                <button class="underline float-right hover:text-gray-600" 
                    title={t.ClearAll()} disabled={isLoading}
                    on:click={clearAll}>{t.Clear()}</button>
            </span> 
        </Input>        
    </Row>
    <Row>
        <Input fieldsetClass="w-[130px]" placeholder={t.ZipPlaceholder()}
            bind:value={zipCode}
            on:input={debounce}
            on:change={debounce}>
            <span slot="label">{zipLabel}</span>
        </Input>
        {#if cities.length <= 1}
            <Input fieldsetClass="w-full" class="!border-gray-100 bg-gray-100" placeholder={t.City()}
                value={(!checksum || cities.length == 0 ? ' ' : `${city}, ${state}`)} disabled>
                <div slot="label">&nbsp;</div>
            </Input>
        {:else}
            <Select fieldsetClass="flex-1"
                bind:value={city}
                on:change={setCity}
            >
                {#each cities as city}
                    <option value={city}>{city}, {state}</option>
                {/each}
                <div slot="label">{t.City()}</div>
            </Select>
        {/if}
    </Row>
</div>